/**
 * Family Portal Notification Service
 * Manages email and push notification delivery for family portal events.
 * Triggers: care statement available, care plan updated, incident (sanitised),
 * activity photo shared, new message from staff.
 */

import LocalStorageService from '../storage/LocalStorageService';

class FamilyNotificationService {
  constructor() {
    this.storagePrefix = 'family_notifications';
    this.channels = ['email', 'push', 'in_app'];
  }

  // ─── Core Dispatch ──────────────────────────────────────────────────

  async dispatch(notification) {
    try {
      const record = {
        id: `NOTIF-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        recipientId: notification.recipientId,
        recipientEmail: notification.recipientEmail,
        type: notification.type,
        title: notification.title,
        body: notification.body,
        data: notification.data || {},
        channels: notification.channels || ['in_app'],
        status: {},
        createdAt: new Date().toISOString(),
        readAt: null,
      };

      for (const channel of record.channels) {
        record.status[channel] = await this._sendViaChannel(channel, record);
      }

      const all = LocalStorageService.get(`${this.storagePrefix}_log`, []);
      all.push(record);
      LocalStorageService.set(`${this.storagePrefix}_log`, all);

      console.info(`[FamilyNotifications] Dispatched: ${record.type}`, { id: record.id, channels: record.channels });
      return { success: true, data: record };
    } catch (error) {
      console.error('[FamilyNotifications] dispatch failed:', error);
      return { success: false, error: error.message };
    }
  }

  // ─── Trigger Helpers ────────────────────────────────────────────────

  async notifyCareStatementAvailable(residentId, statementPeriod) {
    const recipients = await this._getResidentFamilyUsers(residentId);
    const results = [];
    for (const user of recipients) {
      if (!user.preferences?.emailNotifications && !user.preferences?.pushNotifications) continue;
      const channels = this._resolveChannels(user);
      results.push(await this.dispatch({
        recipientId: user.id,
        recipientEmail: user.email,
        type: 'care_statement_available',
        title: 'Monthly Care Statement Available',
        body: `The ${statementPeriod} care statement for ${user.residentName || 'your loved one'} is now available in the portal.`,
        data: { residentId, statementPeriod },
        channels,
      }));
    }
    return { success: true, sent: results.length };
  }

  async notifyCarePlanUpdated(residentId, updateSummary) {
    const recipients = await this._getResidentFamilyUsers(residentId);
    const results = [];
    for (const user of recipients) {
      const channels = this._resolveChannels(user);
      results.push(await this.dispatch({
        recipientId: user.id,
        recipientEmail: user.email,
        type: 'care_plan_updated',
        title: 'Care Plan Updated',
        body: `The care plan for ${user.residentName || 'your loved one'} has been updated: ${updateSummary}`,
        data: { residentId, updateSummary },
        channels,
      }));
    }
    return { success: true, sent: results.length };
  }

  async notifyIncident(residentId, sanitisedDescription) {
    const recipients = await this._getResidentFamilyUsers(residentId);
    const results = [];
    for (const user of recipients) {
      const channels = this._resolveChannels(user);
      results.push(await this.dispatch({
        recipientId: user.id,
        recipientEmail: user.email,
        type: 'incident_notification',
        title: 'Care Update Notification',
        body: sanitisedDescription,
        data: { residentId },
        channels,
      }));
    }
    return { success: true, sent: results.length };
  }

  async notifyActivityPhotoShared(residentId, activityName, photoCount) {
    const recipients = await this._getResidentFamilyUsers(residentId);
    const results = [];
    for (const user of recipients) {
      const channels = this._resolveChannels(user);
      results.push(await this.dispatch({
        recipientId: user.id,
        recipientEmail: user.email,
        type: 'activity_photo_shared',
        title: 'New Activity Photos',
        body: `${photoCount} new photo(s) from "${activityName}" have been shared to the gallery.`,
        data: { residentId, activityName, photoCount },
        channels,
      }));
    }
    return { success: true, sent: results.length };
  }

  async notifyNewStaffMessage(residentId, fromStaffName) {
    const recipients = await this._getResidentFamilyUsers(residentId);
    const results = [];
    for (const user of recipients) {
      const channels = this._resolveChannels(user);
      results.push(await this.dispatch({
        recipientId: user.id,
        recipientEmail: user.email,
        type: 'new_staff_message',
        title: 'New Message from Care Team',
        body: `${fromStaffName} has sent you a message about ${user.residentName || 'your loved one'}.`,
        data: { residentId, fromStaffName },
        channels,
      }));
    }
    return { success: true, sent: results.length };
  }

  // ─── Read / Manage ──────────────────────────────────────────────────

  async getNotifications(recipientId, options = {}) {
    try {
      const all = LocalStorageService.get(`${this.storagePrefix}_log`, []);
      let filtered = all.filter(n => n.recipientId === recipientId);

      if (options.unreadOnly) {
        filtered = filtered.filter(n => !n.readAt);
      }
      if (options.type) {
        filtered = filtered.filter(n => n.type === options.type);
      }

      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const limit = options.limit || 50;
      return { success: true, data: filtered.slice(0, limit), total: filtered.length };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async markAsRead(notificationId) {
    try {
      const all = LocalStorageService.get(`${this.storagePrefix}_log`, []);
      const notif = all.find(n => n.id === notificationId);
      if (notif) {
        notif.readAt = new Date().toISOString();
        LocalStorageService.set(`${this.storagePrefix}_log`, all);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async markAllAsRead(recipientId) {
    try {
      const all = LocalStorageService.get(`${this.storagePrefix}_log`, []);
      const now = new Date().toISOString();
      let count = 0;
      all.forEach(n => {
        if (n.recipientId === recipientId && !n.readAt) {
          n.readAt = now;
          count++;
        }
      });
      LocalStorageService.set(`${this.storagePrefix}_log`, all);
      return { success: true, markedRead: count };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getUnreadCount(recipientId) {
    try {
      const all = LocalStorageService.get(`${this.storagePrefix}_log`, []);
      const count = all.filter(n => n.recipientId === recipientId && !n.readAt).length;
      return { success: true, count };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Push Notification Registration ─────────────────────────────────

  async registerPushSubscription(userId, subscription) {
    try {
      const subs = LocalStorageService.get(`${this.storagePrefix}_push_subs`, []);
      const existing = subs.findIndex(s => s.userId === userId);
      const record = {
        userId,
        endpoint: subscription.endpoint,
        keys: subscription.keys,
        registeredAt: new Date().toISOString(),
        active: true,
      };
      if (existing >= 0) {
        subs[existing] = record;
      } else {
        subs.push(record);
      }
      LocalStorageService.set(`${this.storagePrefix}_push_subs`, subs);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ─── Internal ───────────────────────────────────────────────────────

  async _sendViaChannel(channel, notification) {
    switch (channel) {
      case 'email':
        console.info(`[Email] To: ${notification.recipientEmail} | Subject: ${notification.title}`);
        return 'sent';
      case 'push': {
        const subs = LocalStorageService.get(`${this.storagePrefix}_push_subs`, []);
        const sub = subs.find(s => s.userId === notification.recipientId && s.active);
        if (sub) {
          console.info(`[Push] To endpoint: ${sub.endpoint?.substring(0, 40)}... | ${notification.title}`);
          return 'sent';
        }
        return 'no_subscription';
      }
      case 'in_app':
        return 'delivered';
      default:
        return 'unknown_channel';
    }
  }

  async _getResidentFamilyUsers(residentId) {
    const users = LocalStorageService.get('family_portal_users', []);
    const residents = LocalStorageService.get('residents', []);
    const resident = residents.find(r => r.id === residentId);
    return users
      .filter(u => u.residentId === residentId && u.status === 'active')
      .map(u => ({ ...u, residentName: resident?.preferredName || resident?.name }));
  }

  _resolveChannels(user) {
    const channels = ['in_app'];
    if (user.preferences?.emailNotifications) channels.push('email');
    if (user.preferences?.pushNotifications) channels.push('push');
    return channels;
  }
}

export default new FamilyNotificationService();
